import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { V, useVocab } from "@/lib/vocab";
import { toast } from "sonner";
import { usePaged, ShowMore } from "@/components/Paged";
import { AudiencePicker } from "@/components/AudiencePicker";
import { chipsOf } from "@/lib/audiences";
import { Scale, ScanSearch, CheckCircle2, Trash2, RotateCcw, FileText, ShieldCheck, Loader2 } from "lucide-react";

const TOPICS = [
  ["amenities", "Amenities & facilities"], ["access", "Access, gates & keys"], ["parking", "Parking & vehicles"],
  ["dues_fees", "Dues, assessments & fees"], ["billing", "Account & billing"], ["architectural", "Architectural (ARC)"],
  ["violations", "Rule violations & enforcement"], ["maintenance", "Maintenance & repairs"], ["utilities", "Utilities, trash & water"],
  ["landscaping", "Landscaping & grounds"], ["pets", "Pets & animals"], ["noise_nuisance", "Noise & nuisance"],
  ["safety_security", "Safety & security"], ["governance", "Board, meetings & governance"], ["leasing", "Leasing & rentals"],
  ["events", "Community events & RSVPs"], ["general_info", "General info & contacts"], ["other", "Other / uncategorized"],
];
const TABS = [["needs_review", "Needs review"], ["verified", "Verified"], ["dismissed", "Dismissed"]];

const RuleCard = ({ r, tenantId, readOnly, onChanged }) => {
  const save = async (patch) => { try { await api.put(`/tenants/${tenantId}/rules/${r.id}`, patch); onChanged(); } catch { toast.error("Could not save"); } };
  const setStatus = async (status) => { await save({ status }); toast.success(status === "verified" ? "Verified — the AI can now cite this" : status === "dismissed" ? "Dismissed" : "Restored"); };
  const del = async () => { try { await api.delete(`/tenants/${tenantId}/rules/${r.id}`); toast.success("Deleted"); onChanged(); } catch { toast.error("Could not delete"); } };

  return (
    <div data-testid={`rule-${r.id}`} className="bg-white rounded-2xl border border-sand-200 p-4">
      {readOnly ? <p className="font-semibold text-ink">{r.rule}</p>
        : <textarea data-testid={`rule-text-${r.id}`} defaultValue={r.rule} onBlur={(e) => e.target.value !== r.rule && save({ rule: e.target.value })}
            rows={2} className="w-full font-semibold text-ink bg-transparent rounded-lg border border-transparent hover:border-sand-200 focus:border-redline-400 outline-none p-1 resize-y" />}

      <div className="grid sm:grid-cols-4 gap-2 mt-2">
        <div>
          <label className="text-[10px] uppercase tracking-wide font-bold text-ink/40">Citation</label>
          <input data-testid={`rule-cite-${r.id}`} disabled={readOnly} defaultValue={r.citation} onBlur={(e) => e.target.value !== r.citation && save({ citation: e.target.value })}
            placeholder="e.g. CC&R 4.2" className="w-full mt-0.5 rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-70" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide font-bold text-ink/40">Fine / penalty</label>
          <input data-testid={`rule-fine-${r.id}`} disabled={readOnly} defaultValue={r.fine} onBlur={(e) => e.target.value !== r.fine && save({ fine: e.target.value })}
            placeholder="none stated" className="w-full mt-0.5 rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-70" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide font-bold text-ink/40">Category</label>
          <select disabled={readOnly} value={r.category || "other"} onChange={(e) => save({ category: e.target.value })}
            className="w-full mt-0.5 rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-70">
            {TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide font-bold text-ink/40">AI shares with</label>
          <div className="mt-1" data-testid={`rule-audience-${r.id}`} title="Who the AI may cite this rule to — restricted rules never enter the AI's context for other callers">
            <AudiencePicker tenantId={tenantId} testid={`rule-audience-${r.id}`} compact readOnly={readOnly}
              audiences={chipsOf(r)} onAudiences={(list) => save({ audiences: list })} />
          </div>
        </div>
      </div>

      {r.quote && <p className="text-xs text-ink/45 mt-2 italic border-l-2 border-sand-200 pl-2">“{r.quote}”</p>}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-sand-100">
        <span className="text-xs text-ink/50 inline-flex items-center gap-1.5 flex-wrap">
          <FileText className="h-3.5 w-3.5" /> {r.source_title || "Document"}
          {r.used_count > 0 && (
            <span data-testid={`rule-usage-${r.id}`} title={r.last_used_at ? `Last cited ${new Date(r.last_used_at).toLocaleDateString()}` : `Cited in ${V.lc("person")} answers`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold px-2 py-0.5">
              <ShieldCheck className="h-3 w-3" /> Cited {r.used_count}×
            </span>
          )}
        </span>
        {!readOnly && (
          <div className="flex gap-2">
            {r.status !== "verified" && <button data-testid={`rule-verify-${r.id}`} onClick={() => setStatus("verified")} className="rounded-full bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Verify</button>}
            {r.status === "verified" && <button onClick={() => setStatus("needs_review")} className="rounded-full bg-sand-100 text-ink text-xs font-semibold px-3 py-1.5 hover:bg-sand-200 flex items-center gap-1"><RotateCcw className="h-3.5 w-3.5" /> Unverify</button>}
            {r.status !== "dismissed" ? <button data-testid={`rule-dismiss-${r.id}`} onClick={() => setStatus("dismissed")} className="rounded-full bg-white border border-sand-200 text-ink/60 text-xs font-semibold px-3 py-1.5 hover:bg-sand-50">Dismiss</button>
              : <button onClick={() => setStatus("needs_review")} className="rounded-full bg-sand-100 text-ink text-xs font-semibold px-3 py-1.5 hover:bg-sand-200">Restore</button>}
            <button onClick={del} className="rounded-full bg-white border border-sand-200 text-red-600 p-1.5 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Rules() {
  const { tenantId, role } = useDash();
  const v = useVocab();
  const [tab, setTab] = useState("needs_review");
  const [data, setData] = useCachedState(`rules:${tenantId}`, { rules: [], counts: {} });
  const [docs, setDocs] = useCachedState(`rules-docs:${tenantId}`, []);
  const [scanDoc, setScanDoc] = useState("");
  const [scanning, setScanning] = useState(false);
  const readOnly = role === "board_member";

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/rules`).then((r) => setData(r.data));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);
  const loadDocs = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, [tenantId]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Scans run in the background ingest queue — poll while any are active and toast on finish.
  const scanningDocs = docs.filter((d) => ["queued", "scanning"].includes(d.rules_scan_status));
  const prevScanRef = useRef({});
  useEffect(() => {
    prevScanRef.current = Object.fromEntries(docs.map((d) => [d.id, d.rules_scan_status]));
  }, [docs]);
  useEffect(() => {
    if (!scanningDocs.length || !tenantId) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/tenants/${tenantId}/documents`);
        const fresh = r.data || [];
        const prev = prevScanRef.current;
        fresh.forEach((d) => {
          if (["queued", "scanning"].includes(prev[d.id]) && d.rules_scan_status === "done") {
            toast.success(`Scan finished: "${d.title}"`, {
              description: d.rules_found ? `${d.rules_found} new rule${d.rules_found === 1 ? "" : "s"} ready for review.` : "No new rules found.",
            });
            load();
          }
        });
        setDocs(fresh);
      } catch { /* keep last state */ }
    }, 6000);
    return () => clearInterval(t);
  }, [scanningDocs.length, tenantId, load]);

  const scan = async () => {
    if (!scanDoc) return;
    setScanning(true);
    try {
      await api.post(`/tenants/${tenantId}/documents/${scanDoc}/scan-rules`);
      toast.success("Scan queued", { description: "It runs in the background — you'll get a bell notification with the results." });
      loadDocs();
      setScanDoc("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    setScanning(false);
  };

  const verifyAll = async () => {
    try { const r = await api.post(`/tenants/${tenantId}/rules/verify-all`, {}); toast.success(`Verified ${r.data.verified} rule${r.data.verified === 1 ? "" : "s"}`); load(); }
    catch { toast.error("Could not verify all"); }
  };

  const shown = data.rules.filter((r) => r.status === tab);
  const { visible, remaining, showMore } = usePaged(shown, { step: 25, resetKey: tab });

  return (
    <div className="max-w-[960px] mx-auto">
      <div className="mb-6">
        <PageTitle title={<><Scale className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {v.mod("rules")}</>} about={<>Enforceable, citable rules pulled from your governing documents. Once <span className="font-semibold">verified</span>, the AI answers with the exact rule and its citation — and never guesses on policies or fines.</>} />
      </div>

      {!readOnly && (
        <div className="bg-white rounded-2xl border border-sand-200 p-4 mb-6 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-bold text-ink/50 flex items-center gap-1.5 mb-1"><ScanSearch className="h-3.5 w-3.5 text-redline-500" /> Scan a document for rules</label>
            <select data-testid="rules-scan-doc" value={scanDoc} onChange={(e) => setScanDoc(e.target.value)}
              className="w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
              <option value="">Choose a document…</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
          <button data-testid="rules-scan-btn" onClick={scan} disabled={!scanDoc || scanning}
            className="rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            <ScanSearch className="h-4 w-4" /> {scanning ? "Queueing…" : "Scan for rules"}
          </button>
          {scanningDocs.length > 0 && (
            <div data-testid="rules-scanning-note" className="w-full text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Scanning in the background: {scanningDocs.map((d) => `"${d.title}"`).join(", ")} — you&apos;ll be notified when it&apos;s done.
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex gap-2">
          {TABS.map(([v, l]) => (
            <button key={v} data-testid={`rules-tab-${v}`} onClick={() => setTab(v)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === v ? "bg-ink text-white" : "bg-white border border-sand-200 hover:border-ink text-ink/70"}`}>
              {l} {data.counts[v] ? <span className={tab === v ? "text-white/70" : "text-ink/40"}>({data.counts[v]})</span> : ""}
            </button>
          ))}
        </div>
        {!readOnly && tab === "needs_review" && data.counts.needs_review > 0 && (
          <button data-testid="rules-verify-all" onClick={verifyAll} className="rounded-full bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Verify all</button>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sand-200 p-14 text-center">
          <div className="h-14 w-14 rounded-2xl bg-sand-100 flex items-center justify-center mx-auto mb-3"><Scale className="h-7 w-7 text-ink/30" strokeWidth={1.5} /></div>
          <div className="font-heading text-lg font-bold text-ink">{tab === "needs_review" ? "No rules to review" : tab === "verified" ? "No verified rules yet" : "Nothing dismissed"}</div>
          <div className="text-sm text-ink/50 mt-1 max-w-xs mx-auto">{tab === "needs_review" ? "Scan a governing document above to pull out enforceable rules." : tab === "verified" ? "Verify reviewed rules so the AI can cite them." : "Dismissed rules show up here."}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => <RuleCard key={r.id} r={r} tenantId={tenantId} readOnly={readOnly} onChanged={load} />)}
          <ShowMore remaining={remaining} onClick={showMore} label="Show more rules" />
        </div>
      )}
    </div>
  );
}
